import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiKeys, useGenerateApiKey, useRevokeApiKey } from '../hooks/useApiKeys.js';
import Table from '../components/Table.jsx';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';
import Spinner from '../components/Spinner.jsx';

export default function ApiKeys() {
  const { id: clientId } = useParams();
  const { data, isLoading } = useApiKeys(clientId);
  const generateMutation = useGenerateApiKey(clientId);
  const revokeMutation = useRevokeApiKey(clientId);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: ['orders:write', 'orders:read'] });
  const [newKey, setNewKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPrefix, setNewKeyPrefix] = useState('');
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef(null);

  useEffect(() => {
    if (!helpOpen) return;
    const handler = (e) => { if (helpRef.current && !helpRef.current.contains(e.target)) setHelpOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [helpOpen]);

  const handleGenerate = () => {
    setErr(null);
    if (!form.name.trim()) return setErr('El nombre es obligatorio');
    if (/\s/.test(form.name)) return setErr('El nombre no puede tener espacios');
    generateMutation.mutate(form, {
      onSuccess: (data) => {
        setOpen(false);
        setNewKey(data.key);
        setNewKeyName(form.name);
        // Extraer prefijo del key generado: mp_live_XXXXXXXX_...
        const parts = data.key.split('_');
        setNewKeyPrefix(parts.slice(0, 3).join('_')); // mp_live_XXXXXXXX
        setForm({ name: '', scopes: ['orders:write', 'orders:read'] });
      },
      onError: (e) => setErr(e.response?.data?.message || 'Error al generar'),
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newKey).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleDownload = () => {
    const filename = `token_${newKeyPrefix}_${newKeyName}.txt`;
    const content = [
      `API Key — ${newKeyName}`,
      `Prefijo: ${newKeyPrefix}`,
      `Generada: ${new Date().toLocaleString('es-AR')}`,
      '',
      'KEY (guardar de forma segura, no se puede recuperar):',
      newKey,
      '',
      'Uso en Ideafix (cualquiera de las dos formas funciona):',
      '  Opción A — Header dedicado:',
      '    X-API-Key: ' + newKey,
      '  Opción B — Bearer token estándar:',
      '    Authorization: Bearer ' + newKey,
      '',
      'Endpoint: POST https://mp.mainnet.com.ar/api/create-order',
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: 'key_prefix', header: 'Prefijo', render: (r) => <span className="font-mono text-xs">{r.key_prefix}</span> },
    { key: 'name', header: 'Nombre' },
    { key: 'scopes', header: 'Scopes', render: (r) => (r.scopes || []).join(', ') },
    {
      key: 'status', header: 'Estado',
      render: (r) => r.revoked_at
        ? <span title={`Revocada: ${new Date(r.revoked_at).toLocaleString('es-AR')}`}><Badge variant="danger">Revocada</Badge></span>
        : <Badge variant="success">Activa</Badge>,
    },
    { key: 'last_used_at', header: 'Último uso', render: (r) => r.last_used_at ? new Date(r.last_used_at).toLocaleString('es-AR') : '—' },
    {
      key: 'actions', header: '',
      render: (r) => !r.revoked_at
        ? (
          <Button variant="danger" className="text-xs py-1 px-2"
            onClick={() => {
              if (confirm('¿Revocar esta API key?\n\nLa key dejará de funcionar inmediatamente. El registro queda en auditoría pero no se puede recuperar.')) {
                revokeMutation.mutate(r.id);
              }
            }}
          >
            Revocar
          </Button>
        )
        : <span className="text-xs text-slate-600">Revocada</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/clients/${clientId}`} className="text-slate-400 hover:text-white text-sm">← Cliente</Link>
          <h2 className="text-xl font-semibold">API Keys</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative" ref={helpRef}>
            <button
              onClick={() => setHelpOpen(!helpOpen)}
              className="w-6 h-6 rounded-full bg-bg-elevated border border-border text-slate-400 hover:text-white hover:border-border-strong text-xs font-bold transition-colors flex items-center justify-center"
              title="Ayuda"
            >
              ?
            </button>
            {helpOpen && (
              <div className="absolute right-0 top-8 w-80 bg-bg-surface border border-border rounded-lg shadow-xl z-20 p-4 space-y-2 text-xs text-slate-400">
                <p className="font-medium text-slate-200 mb-1">Sobre las API Keys</p>
                <p><span className="text-slate-300 font-medium">Revocar:</span> Desactiva la key inmediatamente. No la borra — queda en auditoría con la fecha de revocación. No se puede reactivar.</p>
                <p><span className="text-slate-300 font-medium">Usar en Ideafix:</span> Guardala en la BD y enviala en cada request a <code className="text-slate-300">POST /api/create-order</code>.</p>
                <p>Dos formatos válidos:</p>
                <code className="block bg-bg-elevated rounded px-2 py-1 text-slate-300">X-API-Key: mp_live_...</code>
                <code className="block bg-bg-elevated rounded px-2 py-1 text-slate-300">Authorization: Bearer mp_live_...</code>
              </div>
            )}
          </div>
          <Button onClick={() => setOpen(true)}>+ Generar key</Button>
        </div>
      </div>

      {newKey && (
        <div className="card border-emerald-700/50 bg-emerald-900/10 space-y-3">
          <p className="text-sm font-medium text-emerald-300">⚠ Guardá esta key ahora. No se mostrará de nuevo.</p>
          <code className="block bg-bg-elevated rounded px-3 py-2 text-xs font-mono break-all text-slate-100 select-all">
            {newKey}
          </code>
          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" onClick={handleCopy}>{copied ? '✓ Copiado' : 'Copiar'}</Button>
            <Button variant="secondary" onClick={handleDownload}>Descargar</Button>
            <Button variant="ghost" className="text-xs ml-auto" onClick={() => setNewKey(null)}>Cerrar</Button>
          </div>
        </div>
      )}


{isLoading ? <div className="flex justify-center py-10"><Spinner size="lg" /></div>
        : <Table columns={columns} data={data} empty="No hay API keys generadas" />}

      <Modal open={open} onClose={() => setOpen(false)} title="Generar API Key"
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button loading={generateMutation.isPending} onClick={handleGenerate}>Generar</Button></>}
      >
        <Input
          label="Nombre *"
          value={form.name}
          onChange={(e) => setForm(f => ({ ...f, name: e.target.value.replace(/\s/g, '-') }))}
          placeholder="produccion-pos-tienda1"
        />
        <p className="text-xs text-slate-400">
          Sin espacios (se reemplazan por guiones). Usá un nombre descriptivo para identificarla después.
        </p>
        <p className="text-xs text-slate-500">Scopes: orders:write, orders:read (por defecto)</p>
        {err && <p className="text-sm text-red-400">{err}</p>}
      </Modal>
    </div>
  );
}
